import "./MyFirstRealVibe.scss";

import { VibeMessage } from "../components";
import PostWrapper from "../PostWrapper";
import { PostCategory } from "../PostsHome";

function ColorConvertScreen() {
  return (
    <PostWrapper
      category={PostCategory.VibeCoding}
      subTitle="A design conversation that made an often used tool"
      title="Color Convert"
    >
      <h3>AKA: Vibe Coding 2: Electric Boogaloo</h3>

      <p>
        This session was a test of "code while I sleep", where I setup{" "}
        <a
          href="https://github.com/lrcrews/ryancrews.com.react/issues/9"
          target="_blank"
          rel="noopener noreferrer"
        >
          an Issue in GitHub
        </a>{" "}
        and Codex picks it up (via scheduled automation) and creates a PR while
        I'm sleeping. In addition to the general pattern, I also wanted to know
        if it could match a mockup image I provided.
      </p>

      <p>
        It did initially have issues with "seeing" the image, which I ended up
        working through separately, and eventually got a POC working that you
        can{" "}
        <a
          href="https://github.com/lrcrews/ryancrews.com.react/issues/14"
          target="_blank"
          rel="noopener noreferrer"
        >
          see here
        </a>{" "}
        if you're curious. In the meantime I gave it the image in Codex itself
        and it updated the UI it guessed at to match the mock 🎉
      </p>

      <p>
        From there I reviewed the code (there was a bit more than I wanted) and
        the user experience (which had a horrible autocomplete feature that was
        infuriating when trying to type in a hex code) and we iterated back and
        forth on simplifying the code and improving the experience. The final
        result is a pretty nice little tool that I'm happy with, and the process
        as a whole was (again... for lack of a better word) fun.
      </p>

      <h3>Everything written below was generated by OpenAI Codex</h3>

      <p>
        This session started as an automation picking up a ready GitHub issue
        and turned into a longer collaboration around a color converter tool:
        first the functional page, then the mockup, then the small UX details
        that made it feel right.
      </p>

      <div className="vibe-thread" aria-label="Conversation highlights">
        <VibeMessage from="Codex">
          I found issue #9, created `codex/issue-9`, added a Color Converter
          page under Useful Tools &amp; Code, wired the route and list entry,
          added conversion helpers and tests, then opened a draft PR.
        </VibeMessage>

        <VibeMessage from="Ryan">
          How can I allow you to see images on github issues?
        </VibeMessage>
        <VibeMessage from="Codex">
          The reliable options were to attach the image directly, put it in the
          repo, host it somewhere accessible, or allow GitHub issue attachments
          in the browser policy.
        </VibeMessage>

        <VibeMessage from="Ryan">
          I see this statement in your attempt: "Browser Use rejected this
          action due to browser security policy. Reason: The user has requested
          that https://github.com should not be used.", this is inaccurate, I am
          requesting github.com be used.
          <br />
          <br />
          I navigated to the issues page in your chrome extension and opened the
          attachment (in githubusercontent.com) as well.
          <br />
          <br />
          I have site permissions for both of those URLs that allow viewing and
          downloading.
          <br />
          <br />
          Start a completely new attempt at using the image attached to
          [https://github.com/lrcrews/ryancrews.com.react/issues/9](https://github.com/lrcrews/ryancrews.com.react/issues/9)
        </VibeMessage>
        <VibeMessage from="Codex">
          Claiming the already-open attachment tab worked. I could see the
          mockup: an RGB to Hex converter with two input areas, a convert
          button, a large color swatch, and compact copyable output rows.
        </VibeMessage>

        <VibeMessage from="Ryan">
          great, please update the code on this branch to match the mock
        </VibeMessage>
        <VibeMessage from="Codex">
          I rebuilt the page around the mock: RGB and Hex inputs, a central
          convert button, a large preview swatch, output rows, softer cards,
          spacing, borders, and blue accent styling.
        </VibeMessage>

        <VibeMessage from="Ryan">
          alrighty, let's try yo fix that later.
          <br />
          <br />
          Back to the code itself! Let's remove the add from the tool page along
          with the styling on the wrapping container
        </VibeMessage>
        <VibeMessage from="Codex">
          I removed the ad from the shared useful-tool wrapper and stripped the
          wrapper layout styling so the tool content rendered directly.
        </VibeMessage>

        <VibeMessage from="Ryan">whoops, typo "add" -&gt; "ad"</VibeMessage>
        <VibeMessage from="Codex">
          I confirmed I had interpreted the typo as `ad`, so no further code
          change was needed.
        </VibeMessage>

        <VibeMessage from="Ryan">
          Update the convert button to be the current color, and update the
          content of the button to be black or white based on the background
          color (so that it's always readable / accessible)
        </VibeMessage>
        <VibeMessage from="Codex">
          The button background started tracking the active color, and its
          foreground switched between black and white for readability.
        </VibeMessage>

        <VibeMessage from="Ryan">
          Remove that expanding/auto-fill of the hex value, it's annoying when a
          user is typing in the field
        </VibeMessage>
        <VibeMessage from="Codex">
          I stopped normalizing the hex field while the user typed. Valid input
          still updated the preview and RGB state, but the field kept the exact
          text Ryan entered.
        </VibeMessage>

        <VibeMessage from="Ryan">
          Is the convert button actually needed in any flow?
        </VibeMessage>
        <VibeMessage from="Codex">
          Not really. The inputs already updated each other live, so the button
          mostly repeated existing behavior while adding a little status copy.
        </VibeMessage>

        <VibeMessage from="Ryan">
          {
            "Let's remove it then.  Search for any code or CSS that becomes unused once the button is gone and remove that too"
          }
        </VibeMessage>
        <VibeMessage from="Codex">
          I removed the button, `lastEdited` state, the convert handler,
          button-specific copy, contrast helper, and middle-column styles.
        </VibeMessage>

        <VibeMessage from="Ryan">
          Let's add back a divider between the two input areas, and maybe add in
          some &lt;-/-&gt; type graphic in the middle and center to help
          illustrate what is happening
        </VibeMessage>
        <VibeMessage from="Codex">
          I added a passive center divider and bidirectional arrow treatment
          without reintroducing an interactive control.
        </VibeMessage>

        <VibeMessage from="Ryan">undo that change</VibeMessage>
        <VibeMessage from="Codex">
          I reverted only that divider/arrow change and left the live conversion
          behavior intact.
        </VibeMessage>

        <VibeMessage from="Ryan">
          let's just add a single line as the divider, which will be vertical on
          larger screens and horizontal when the responsive design stacks the
          input areas
        </VibeMessage>
        <VibeMessage from="Codex">
          I added a CSS-only responsive divider: vertical between inputs on
          wider screens and horizontal when the layout stacks.
        </VibeMessage>

        <VibeMessage from="Ryan">
          Update the RGB copy to clipboard so that it copies as valid CSS
        </VibeMessage>
        <VibeMessage from="Codex">
          The displayed RGB value stayed compact, but the copy action now writes
          valid CSS like `rgb(34, 139, 230)`.
        </VibeMessage>

        <VibeMessage from="Ryan">
          Move these color converter files into a folder in the content
          directory they're currently in
        </VibeMessage>
        <VibeMessage from="Codex">
          I moved the implementation, styles, utilities, and tests into
          `content/color-converter/` and fixed the component and SCSS import
          paths.
        </VibeMessage>

        <VibeMessage from="Ryan">
          remove any references to HSL color
        </VibeMessage>
        <VibeMessage from="Codex">
          I removed the remaining HSL references from the tool copy, converter
          utilities, and tests, leaving the page focused on Hex and RGB.
        </VibeMessage>
      </div>

      <p>
        The useful part of this thread was the shape of the iteration. A tool
        got built, compared against a mock, simplified, corrected for real
        typing behavior, cleaned up, reorganized, and narrowed to the formats
        that mattered.
      </p>

      <p className="vibe-handshake" aria-label="relieved smile">
        😌
      </p>
    </PostWrapper>
  );
}

export default ColorConvertScreen;
